import React, { useRef, useEffect, useState } from "react";
import { Box, Typography, Stack, useTheme } from "@mui/material";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import SentimentNeutralIcon from "@mui/icons-material/SentimentNeutral";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import { formatChartTitle } from "../../utils";

interface RiskBreakdownData {
  high: number;
  medium: number;
  low: number;
}

interface RiskBreakdownChartProps {
  data: RiskBreakdownData;
  total: number;
  participationRate: number;
  title?: string;
  height?: number;
  hideTotal?: boolean;
  fullWidthLegend?: boolean;
}

export const RiskBreakdownChart: React.FC<RiskBreakdownChartProps> = ({
  data,
  total,
  participationRate,
  title,
  height = 150,
  hideTotal = false,
  fullWidthLegend = false,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const totalRisk = data.high + data.medium + data.low;
  const formattedTitle = formatChartTitle(title);

  const highPercentage = totalRisk > 0 ? (data.high / totalRisk) * 100 : 0;
  const mediumPercentage = totalRisk > 0 ? (data.medium / totalRisk) * 100 : 0;
  const lowPercentage = totalRisk > 0 ? (data.low / totalRisk) * 100 : 0;

  return (
    <Box ref={containerRef} sx={{ width: "100%" }}>
      {formattedTitle && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {formattedTitle}
        </Typography>
      )}
      <Stack spacing={2}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {!hideTotal && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                {totalRisk}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                / {total}
              </Typography>
            </Box>
          )}
          {!hideTotal && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2" color="primary.main">
                Participation Rate: {participationRate}%
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            p: 1,
            borderRadius: 1,
            height: "70px",
            backgroundColor: theme.palette.action.hover,
          }}
        >
          {data.high > 0 && (
            <Box
              sx={{
                background: theme.palette.error.main,
                width: `${highPercentage}%`,
                height: "100%",
                borderRadius: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: data.high > 0 ? "4px" : 0,
              }}
            />
          )}
          {data.medium > 0 && (
            <Box
              sx={{
                background: theme.palette.warning.main,
                width: `${mediumPercentage}%`,
                height: "100%",
                borderRadius: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: data.medium > 0 ? "4px" : 0,
              }}
            />
          )}
          {data.low > 0 && (
            <Box
              sx={{
                background: theme.palette.success.main,
                width: `${lowPercentage}%`,
                height: "100%",
                borderRadius: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: data.low > 0 ? "4px" : 0,
              }}
            />
          )}
        </Box>
        <Stack direction="row" spacing={3} justifyContent="center">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
              width: fullWidthLegend ? "100%" : "auto",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              At High Risk{" "}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <SentimentVeryDissatisfiedIcon
                sx={{ color: theme.palette.error.main, fontSize: 20 }}
              />
              <Typography variant="subtitle2">{data.high}</Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
              width: fullWidthLegend ? "100%" : "auto",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              At Medium Risk{" "}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <SentimentNeutralIcon
                sx={{ color: theme.palette.warning.main, fontSize: 20 }}
              />
              <Typography variant="subtitle2">{data.medium}</Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
              width: fullWidthLegend ? "100%" : "auto",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              At Low Risk{" "}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <SentimentVerySatisfiedIcon
                sx={{ color: theme.palette.success.main, fontSize: 20 }}
              />
              <Typography variant="subtitle2">{data.low}</Typography>
            </Box>
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};
